// src/pages/clubs/CreateClub.jsx
// Lets an existing (post-onboarding) user create a new club.
// Route: /clubs/create

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDoc, collection, doc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { uploadBlobToStorage } from '@/services/storageService';
import SquareLogoUploader from '@/components/inputs/SquareLogoUploader.jsx';
import { Avatar } from '@/components/avatar';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon, ArrowPathIcon, TrashIcon,
  TrophyIcon, CheckCircleIcon, ArrowRightIcon, SparklesIcon,
} from '@heroicons/react/24/outline';
import { findAttachableTeamsForUser, attachTeamsToClub } from '@/services/clubService';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

const inputCls = 'w-full rounded-xl border border-zinc-300 bg-white px-3.5 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition';

export default function CreateClub() {
  const { firebaseUser } = useAuth();
  const navigate = useNavigate();

  const [name,    setName]    = useState('');
  const [sport,   setSport]   = useState('');
  const [website, setWebsite] = useState('');
  const [email,   setEmail]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const SPORTS = ['Soccer', 'Basketball', 'Baseball', 'Softball', 'Football', 'Volleyball', 'Lacrosse', 'Hockey', 'Other'];

  const [processedLogo, setProcessedLogo] = useState(null);
  const [logoPreview,   setLogoPreview]   = useState(null);

  // Two-step flow: 'form' → optional 'attach' → navigate
  // After the club is created, if the founder owns/coaches existing
  // teams that aren't yet linked to any club, we offer to attach them
  // in a step-2 panel rather than dropping them on a blank dashboard.
  // This closes the gap where pre-club teams were invisible to club
  // surfaces (e.g. ClubBroadcast saying "no teams in this club").
  const [step,               setStep]               = useState('form');
  const [createdClub,        setCreatedClub]        = useState(null); // { id, name }
  const [attachableTeams,    setAttachableTeams]    = useState([]);
  const [selectedToAttach,   setSelectedToAttach]   = useState(new Set());
  const [attachInFlight,     setAttachInFlight]     = useState(false);

  const handleRemoveLogo = () => {
    setProcessedLogo(null);
    setLogoPreview(null);
  };

  const goToClub = (clubId) => navigate(`/clubs/${clubId}`);

  const handleSkipAttach = () => {
    if (createdClub?.id) goToClub(createdClub.id);
  };

  const handleAttachSelected = async () => {
    if (!createdClub?.id || !selectedToAttach.size) {
      handleSkipAttach();
      return;
    }
    setAttachInFlight(true);
    try {
      const ids = Array.from(selectedToAttach);
      const { attached, skipped } = await attachTeamsToClub({
        clubId: createdClub.id,
        teamIds: ids,
      });
      if (attached.length) {
        toast.success(
          attached.length === 1
            ? '1 team attached to your club'
            : `${attached.length} teams attached to your club`
        );
      }
      if (skipped.length) {
        toast(
          skipped.length === 1
            ? `1 team was skipped (already in another club)`
            : `${skipped.length} teams were skipped (already in another club)`
        );
      }
      goToClub(createdClub.id);
    } catch (err) {
      console.error('[CreateClub] attach failed', err);
      toast.error('Could not attach teams. Skipping for now — you can attach them later in Club Settings.');
      goToClub(createdClub.id);
    } finally {
      setAttachInFlight(false);
    }
  };

  const toggleAttach = (teamId) => {
    setSelectedToAttach((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Club name is required.'); return; }
    if (!firebaseUser?.uid) { toast.error('You must be signed in.'); return; }

    setSaving(true);
    try {
      const uid = firebaseUser.uid;

      // Upload logo if provided
      let logoUrl = '';
      if (processedLogo?.blob) {
        const tempId = crypto.randomUUID();
        const path = `clubLogos/${uid}_${tempId}/logo_${Date.now()}.webp`;
        const { downloadURL } = await uploadBlobToStorage(processedLogo.blob, path, {
          contentType: processedLogo.blob.type,
        });
        logoUrl = downloadURL;
      }

      // Create the club doc
      const clubRef = await addDoc(collection(db, 'clubs'), {
        name:         name.trim(),
        sport:        sport || null,
        website:      website.trim(),
        contactEmail: email.trim(),
        logoUrl:      logoUrl || null,
        ownerId:      uid,
        teamIds:      [],
        createdAt:    serverTimestamp(),
        updatedAt:    serverTimestamp(),
      });
      const clubId = clubRef.id;

      // Seed owner as club member
      await setDoc(doc(db, 'clubs', clubId, 'members', uid), {
        uid,
        email:       firebaseUser.email || '',
        displayName: firebaseUser.displayName || '',
        role:        'owner',
        addedAt:     serverTimestamp(),
      });

      // Update user profile with clubId
      await setDoc(doc(db, 'users', uid), { clubId }, { merge: true });

      toast.success(`${name.trim()} created!`);

      // Step 2: scan for existing teams the user manages that aren't yet
      // in any club. If we find any, offer to attach them. Otherwise jump
      // straight to the club dashboard (preserves the original UX for
      // brand-new users with no prior teams).
      try {
        const candidates = await findAttachableTeamsForUser({ uid, clubId });
        const unattached = candidates.filter((t) => !t.clubId);
        if (unattached.length > 0) {
          setCreatedClub({ id: clubId, name: name.trim() });
          setAttachableTeams(unattached);
          setSelectedToAttach(new Set(unattached.map((t) => t.id))); // default: all checked
          setStep('attach');
          return; // stay on page; user resolves the attach step
        }
      } catch (scanErr) {
        // Non-fatal — if the scan fails (e.g. transient network), just
        // proceed to the dashboard. The user can still attach manually
        // from ClubSettings later.
        console.warn('[CreateClub] attachable scan failed', scanErr);
      }

      navigate(`/clubs/${clubId}`);
    } catch (err) {
      console.error('[CreateClub]', err);
      toast.error('Failed to create club. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Step 2: Attach existing teams ──
  // Shown after a successful club creation IFF the user owns/coaches
  // teams that aren't yet linked to any club. The form path collapses
  // into a confirmation panel; nothing else mounts in this state.
  if (step === 'attach' && createdClub) {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="min-h-[60vh] flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md">

            {/* Header — celebratory, mirrors AcceptInvite tone */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-4">
                <SparklesIcon className="h-7 w-7 text-[#5a8a1f]" />
              </div>
              <p
                className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {createdClub.name} is live
              </p>
              <h1
                className="mt-1 text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                Attach your teams
              </h1>
              <p className="text-sm text-zinc-500 mt-2">
                We found {attachableTeams.length} team{attachableTeams.length === 1 ? '' : 's'} you manage that {attachableTeams.length === 1 ? 'isn\'t' : 'aren\'t'} linked to a club yet. Pick the ones to bring into <span className="font-semibold text-[#0d1f14]">{createdClub.name}</span>.
              </p>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-4">
              <ul className="space-y-2">
                {attachableTeams.map((t) => {
                  const checked = selectedToAttach.has(t.id);
                  return (
                    <li key={t.id}>
                      <label
                        className={`flex items-center gap-3 rounded-xl border px-3 py-3 cursor-pointer transition-colors ${
                          checked
                            ? 'border-[#b5f03d]/40 bg-[#b5f03d]/5'
                            : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAttach(t.id)}
                          disabled={attachInFlight}
                          className="h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                        />
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 overflow-hidden">
                          {t.logoUrl ? (
                            <img src={t.logoUrl} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <TrophyIcon className="h-4 w-4 text-[#5a8a1f]" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-[#0d1f14] truncate">{t.name}</p>
                          <p className="text-xs text-zinc-500 truncate">
                            {[t.sport, t.ageGroup, t.gender].filter(Boolean).join(' · ') || 'Team'}
                          </p>
                        </div>
                        {checked && (
                          <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f] shrink-0" />
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleAttachSelected}
                  disabled={attachInFlight}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {attachInFlight ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Attaching…
                    </>
                  ) : (
                    <>
                      Attach {selectedToAttach.size > 0 ? `${selectedToAttach.size} ` : ''}
                      team{selectedToAttach.size === 1 ? '' : 's'}
                      <ArrowRightIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSkipAttach}
                  disabled={attachInFlight}
                  className="inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-zinc-600 hover:border-[#b5f03d] hover:text-[#0d1f14] transition-all disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Skip
                </button>
              </div>

              <p className="text-[11px] text-zinc-400 text-center">
                You can attach more teams later in Club Settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 1: Create form ──
  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-4">
              <BuildingOffice2Icon className="h-7 w-7 text-[#5a8a1f]" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>New Organization</p>
            <h1 className="mt-1 text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Create a Club</h1>
            <p className="text-sm text-zinc-500 mt-2">
              Group your teams under one organization to manage them together.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 space-y-5">

            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-[#0d1f14] mb-1">
                Club / Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={inputCls}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Eastside United FC"
                required
                autoFocus
              />
            </div>

            {/* Sport */}
            <div>
              <label className="block text-sm font-semibold text-[#0d1f14] mb-1">
                Primary Sport <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <select
                className={inputCls}
                value={sport}
                onChange={e => setSport(e.target.value)}
              >
                <option value="">Multi-sport / not specified</option>
                {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="text-xs text-zinc-400 mt-1">
                If set, all teams under this club will inherit this sport automatically.
              </p>
            </div>

            {/* Logo */}
            <div>
              <label className="block text-sm font-semibold text-[#0d1f14] mb-1">
                Club Logo <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <p className="text-xs text-zinc-400 mb-3">
                Shown on all teams under this club. Teams can also upload their own logo.
              </p>
              <div className="flex items-center gap-4">
                <Avatar
                  src={logoPreview}
                  alt="Club logo preview"
                  square
                  className="!h-14 !w-14 flex-shrink-0 border border-zinc-200 p-0.5 bg-zinc-50"
                  initials={name?.charAt(0)?.toUpperCase() || 'C'}
                />
                <div className="flex-1 space-y-1.5">
                  <SquareLogoUploader
                    label="Choose logo"
                    initialPreview={logoPreview}
                    maxSize={512}
                    mimeType="image/webp"
                    quality={0.9}
                    showPreview={false}
                    bordered
                    caption={null}
                    onProcessed={(res) => {
                      setProcessedLogo(res);
                      setLogoPreview(res.dataUrl);
                    }}
                  />
                  {logoPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="h-3 w-3" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-semibold text-[#0d1f14] mb-1">
                Website <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                className={inputCls}
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="https://yourclub.com"
              />
            </div>

            {/* Contact email */}
            <div>
              <label className="block text-sm font-semibold text-[#0d1f14] mb-1">
                Contact Email <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="info@yourclub.com"
              />
            </div>

            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-3 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 mt-2"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {saving ? 'Creating…' : 'Create Club'}
            </button>
          </form>

          <p className="text-center text-xs text-zinc-400 mt-4">
            You'll be the owner. You can invite other coaches after creation.
          </p>
        </div>
      </div>
    </div>
  );
}
